import { createDrawerNavigator } from "@react-navigation/drawer";
import { NavigationContainer } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import React from "react";
import firebase from "firebase/app";
import Bugsnag, { Event } from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import { ActivityIndicator, StyleSheet, useWindowDimensions, View } from "react-native";
import { useSelector } from "react-redux";
import TagManager from "react-gtm-module";

import "./firebase";
import * as userDb from "src/db/user.db";
import { kRoute, kStorefrontRouteNames } from "./kRoute";
import { useAppDispatch, withRedux } from "./redux";
import { useFetchCompany, useFetchPromotions } from "./redux/company/actions";
import { useLoadConfigs } from "./redux/configs/actions";
import { useFetchDraftCarts } from "./redux/draftCart/thunk";
import { useErrorHandler } from "./redux/errorHandler/actions";
import { useFetchMessages } from "./redux/messages/actions";
import { kWindowBreakpoints, useResizeListener } from "./redux/navigation/thunkActions";
import { logout, useFetchCart, useFirebaseAuth } from "./redux/user/actions";
import { useFetchProducts } from "./redux/firebaseProducts/thunkActions";
import {
  P00,
  P01,
  P01_01,
  P01_02,
  P01_03,
  P01_04,
  P01_05,
  P01_06,
  P02,
  P03,
  P03_01,
  P03_02,
  P03_03,
  P03_04,
  P04,
  P04_01,
  P05,
  P06,
  P06_05,
  P06_09,
  P06_10,
  P06_11,
  P07,
  P08,
  P08_01,
  P08_02,
  P09,
  P09_01,
  P10,
  P100,
  P101,
  P102,
  P103,
  P104,
  P105,
  P11,
  P12,
  P13,
  P14,
  P16,
  P16_01,
  P17,
  P18,
  P19,
  P20,
  P21,
  P99,
} from "./screens";
import { getPageTitle } from "./kPageTitles";
import { kConfig } from "./kRouteConfig";
import { kColors } from "./kColors";
import { kStyleConst } from "./kStyle";
import { version } from "../package.json";
import { navigationRef, useRootNavigation } from "./RootNavigation";
import { OverlayProvider, ToastProvider } from "./providers";
import { DrawerContent, StackHeader } from "./components/navigator";
import { useUserProfile } from "./hooks/ui";

import "./app.css";
import { kEnv } from "./env";
import { AlertModal, tAlertModalProps } from "./components/Modals/AlertModal";
import { openModalAsync } from "./redux/modals/actions";

const isEmailCampaignError = (event: Event) => {
  if (event.originalError instanceof Error) {
    return event.originalError.message.includes("Object Not Found Matching Id:");
  } else if (typeof event.originalError === "string") {
    return event.originalError.includes("Object Not Found Matching Id:");
  }
  return false;
};

if (!__DEV__) {
  Bugsnag.start({
    releaseStage: kEnv === "prod" ? "production" : kEnv === "test" ? "staging" : "development",
    appVersion: version,
    apiKey: "03d1935a14abb539320509f637ac2a68",
    plugins: [new BugsnagPluginReact()],
    onError: (event) => {
      return !isEmailCampaignError(event);
    },
    // do this data gathering on the reducers for the store not here.
    // onError: (error) => {
    //   const {
    //     UserReducer: {
    //       profile: { accountType, firstName, lastName, companyRefPath, role },
    //     },
    //     ...rest
    //   }: tReduxState = store.getState();
    //   error.addMetadata("userDetail", { accountType, firstName, lastName, companyRefPath, role, state: rest });
    // },
  });
}

const ErrorBoundary = Bugsnag && Bugsnag.getPlugin("react")?.createErrorBoundary(React);

TagManager.initialize({ gtmId: "GTM-MH9XVNL" });

// import { YellowBox } from "react-native";
// YellowBox.ignoreWarnings(["Warning: "]);
// console.disableYellowBox = true;
// import { LogBox } from "react-native";
// LogBox.ignoreAllLogs();

const Drawer = createDrawerNavigator();
const Stack = createStackNavigator();

const LoginStack: React.FC = () => {
  return (
    <Stack.Navigator
      initialRouteName={kRoute.P01}
      screenOptions={{
        header: (props) => <StackHeader {...props} />,
        cardStyle: styles.gStackScreenDefault,
      }}
    >
      <Stack.Screen name={kRoute.P01} component={P01} options={{ title: getPageTitle("P01") }} />
      <Stack.Screen name={kRoute.P01_01} component={P01_01} options={{ title: getPageTitle("P01_01") }} />
      <Stack.Screen name={kRoute.P01_02} component={P01_02} options={{ title: getPageTitle("P01_02") }} />
      <Stack.Screen name={kRoute.P01_03} component={P01_03} options={{ title: getPageTitle("P01_03") }} />
      <Stack.Screen name={kRoute.P01_04} component={P01_04} options={{ title: getPageTitle("P01_04") }} />
      <Stack.Screen name={kRoute.P01_05} component={P01_05} options={{ title: getPageTitle("P01_05") }} />
      <Stack.Screen name={kRoute.P01_06} component={P01_06} options={{ title: getPageTitle("P01_06") }} />
      <Stack.Screen name={kRoute.P00} component={P00} options={{ title: getPageTitle("P00") }} />
      <Stack.Screen name={kRoute.P105} component={P105} options={{ title: getPageTitle("P105") }} />
    </Stack.Navigator>
  );
};

const LoggedStack: React.FC = () => {
  const { isAdmin, isBrand, isRetailer, isRep } = useUserProfile();

  const initialRouteName = isRetailer ? kRoute.P99 : kRoute.P02;

  return (
    <Stack.Navigator
      initialRouteName={initialRouteName}
      screenOptions={{
        header: (props) => <StackHeader {...props} />,
        cardStyle: styles.gStackScreenDefault,
      }}
    >
      <Stack.Screen name={kRoute.P02} component={P02} options={{ title: getPageTitle("P02") }} />
      <Stack.Screen name={kRoute.P03} component={P03} options={{ title: getPageTitle("P03") }} />
      <Stack.Screen name={kRoute.P03_01} component={P03_01} options={{ title: getPageTitle("P03_01") }} />
      {!isRetailer && (
        <Stack.Screen name={kRoute.P03_02} component={P03_02} options={{ title: getPageTitle("P03_02") }} />
      )}
      {isBrand && <Stack.Screen name={kRoute.P03_03} component={P03_03} options={{ title: getPageTitle("P03_03") }} />}
      {(isBrand || isRep) && (
        <Stack.Screen name={kRoute.P03_04} component={P03_04} options={{ title: getPageTitle("P03_04") }} />
      )}
      <Stack.Screen name={kRoute.P04} component={P04} options={{ title: getPageTitle("P04") }} />
      <Stack.Screen name={kRoute.P04_01} component={P04_01} options={{ title: getPageTitle("P04_01") }} />
      {!isRetailer && <Stack.Screen name={kRoute.P06} component={P06} options={{ title: getPageTitle("P06") }} />}
      <Stack.Screen name={kRoute.P06_05} component={P06_05} options={{ title: getPageTitle("P06_05") }} />
      <Stack.Screen name={kRoute.P06_09} component={P06_09} options={{ title: getPageTitle("P06_09") }} />
      {isAdmin && <Stack.Screen name={kRoute.P06_10} component={P06_10} options={{ title: getPageTitle("P06_10") }} />}
      <Stack.Screen name={kRoute.P06_11} component={P06_11} options={{ title: getPageTitle("P06_11") }} />
      {isBrand && <Stack.Screen name={kRoute.P12} component={P12} options={{ title: getPageTitle("P12") }} />}
      {isAdmin && <Stack.Screen name={kRoute.P13} component={P13} options={{ title: getPageTitle("P13") }} />}
      {isAdmin && <Stack.Screen name={kRoute.P14} component={P14} options={{ title: getPageTitle("P14") }} />}
      {isRep && <Stack.Screen name={kRoute.P17} component={P17} options={{ title: getPageTitle("P17") }} />}
      {isRep && <Stack.Screen name={kRoute.P18} component={P18} options={{ title: getPageTitle("P18") }} />}
      {isRetailer && <Stack.Screen name={kRoute.P19} component={P19} options={{ title: getPageTitle("P19") }} />}
      <Stack.Screen name={kRoute.P05} component={P05} options={{ title: getPageTitle("P05") }} />
      <Stack.Screen name={kRoute.P07} component={P07} options={{ title: getPageTitle("P07") }} />
      <Stack.Screen name={kRoute.P08} component={P08} options={{ title: getPageTitle("P08") }} />
      <Stack.Screen name={kRoute.P08_01} component={P08_01} options={{ title: getPageTitle("P08_01") }} />
      {isBrand && <Stack.Screen name={kRoute.P08_02} component={P08_02} options={{ title: getPageTitle("P08_02") }} />}
      {!isAdmin && <Stack.Screen name={kRoute.P09} component={P09} options={{ title: getPageTitle("P09") }} />}
      {!isAdmin && <Stack.Screen name={kRoute.P09_01} component={P09_01} options={{ title: getPageTitle("P09_01") }} />}
      {!isRetailer && <Stack.Screen name={kRoute.P10} component={P10} options={{ title: getPageTitle("P10") }} />}
      {isAdmin && <Stack.Screen name={kRoute.P11} component={P11} options={{ title: getPageTitle("P11") }} />}
      {isAdmin && <Stack.Screen name={kRoute.P16} component={P16} options={{ title: getPageTitle("P16") }} />}
      {isAdmin && <Stack.Screen name={kRoute.P16_01} component={P16_01} options={{ title: getPageTitle("P16_01") }} />}
      {!isRep && <Stack.Screen name={kRoute.P20} component={P20} options={{ title: getPageTitle("P20") }} />}
      {isRetailer && <Stack.Screen name={kRoute.P21} component={P21} options={{ title: getPageTitle("P21") }} />}
      <Stack.Screen name={kRoute.P99} component={P99} options={{ title: getPageTitle("P99") }} />
      <Stack.Screen name={kRoute.P100} component={P100} options={{ title: getPageTitle("P100") }} />
      <Stack.Screen name={kRoute.P101} component={P101} options={{ title: getPageTitle("P101") }} />
      {isRetailer && <Stack.Screen name={kRoute.P102} component={P102} options={{ title: getPageTitle("P102") }} />}
      {!isRetailer && <Stack.Screen name={kRoute.P103} component={P103} options={{ title: getPageTitle("P103") }} />}
      <Stack.Screen name={kRoute.P00} component={P00} />
    </Stack.Navigator>
  );
};

const LoggedDrawerStack: React.FC = () => {
  const dimensions = useWindowDimensions();
  const { isBrand } = useUserProfile();
  const dispatch = useAppDispatch();
  const rIsLoading = useSelector((state) => state.LoadingReducer.userFetching);
  const currentRouteName = useSelector((state) => state.NavigationReducer.currentRouteName);
  const userId = useSelector((state) => state.UserReducer.profile.uid);
  useFetchCart();
  useFetchPromotions();

  const isStorefrontRouteName = currentRouteName && kStorefrontRouteNames.includes(currentRouteName);
  const isMobile = dimensions.width < kWindowBreakpoints.md;
  const drawerType = isStorefrontRouteName || isMobile || currentRouteName === kRoute.P104 ? "front" : "permanent";

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    if (userId) {
      unsubscribe = userDb.documentUser(userId).onSnapshot((document) => {
        if (document.data()?.isDeactivated) {
          dispatch(logout());
          dispatch(
            openModalAsync<tAlertModalProps>(AlertModal, {
              title: "Account deactivated",
              description:
                "Your account is currently deactivated. If you have any questions, please contact support via email.",
            }),
          );
        }
      });
    }

    return () => {
      unsubscribe?.();
    };
  }, [dispatch, userId]);

  if (rIsLoading) {
    return (
      <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
        <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
      </View>
    );
  }

  return (
    <Drawer.Navigator
      drawerType={drawerType}
      drawerContent={(props) => <DrawerContent {...props} />}
      drawerStyle={styles.gDrawerWidth}
      sceneContainerStyle={styles.gStackScreenDefault}
    >
      <Drawer.Screen name="/" component={LoggedStack} />
      {isBrand && <Drawer.Screen name={kRoute.P104} component={P104} options={{ title: getPageTitle("P104") }} />}
    </Drawer.Navigator>
  );
};

const App: React.FC = () => {
  const isLoggedIn = useSelector(({ UserReducer }) => UserReducer.isLoggedIn);
  const dispatch = useAppDispatch();
  const routeNameRef = React.useRef<string>();
  useRootNavigation();
  useFirebaseAuth();
  useFetchCompany();
  useFetchMessages();
  useErrorHandler();
  useFetchDraftCarts();
  useResizeListener();
  useFetchProducts();
  useLoadConfigs();

  const handleOnReady = () => {
    const currentRouteName = navigationRef?.current?.getCurrentRoute()?.name ?? "";
    dispatch({ type: "SET_CURRENT_ROUTE_NAME", payload: currentRouteName });
    routeNameRef.current = currentRouteName;
  };

  const handleOnStateChange = async () => {
    const previousRouteName = routeNameRef.current;
    const currentRouteName = navigationRef?.current.getCurrentRoute()?.name ?? "";
    dispatch({ type: "SET_CURRENT_ROUTE_NAME", payload: currentRouteName });

    if (previousRouteName !== currentRouteName) {
      Bugsnag.leaveBreadcrumb(`Navigation: ${previousRouteName} -> ${currentRouteName}`);
      firebase.analytics().logEvent("screen_view", {
        firebase_screen: currentRouteName,
        firebase_screen_class: previousRouteName,
      });
    }
    routeNameRef.current = currentRouteName;
  };

  return (
    <NavigationContainer
      documentTitle={{ formatter: (options) => options?.title || "Market | bright.blue" }}
      linking={{
        prefixes: ["https://*themaintab*.com", "http://*themaintab*.com", "http:*:000", "https:*:000"],
        enabled: true,
        config: kConfig,
      }}
      ref={navigationRef}
      onReady={handleOnReady}
      onStateChange={handleOnStateChange}
    >
      <ToastProvider>
        <OverlayProvider>{isLoggedIn ? <LoggedDrawerStack /> : <LoginStack />}</OverlayProvider>
      </ToastProvider>
    </NavigationContainer>
  );
};

const _App: typeof App = () => (
  <>
    {ErrorBoundary ? (
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    ) : (
      <App />
    )}
  </>
);
export default withRedux(_App);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  flex1: {
    flex: 1,
  },
  gDrawerWidth: {
    width: kStyleConst.kDrawerWidth,
  },
  gStackScreenDefault: {
    backgroundColor: kColors.white,
    flex: 1,
  },
  justifyCenter: {
    justifyContent: "center",
  },
});
